/**
 * ApPaymentVoucher — Printable transaction voucher for AP Payments.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { ApPaymentDto, ApPaymentLineDto, ApPaymentAllocationDto } from '@/types/apPayment.types';
import { PrintableTable } from '@/components/master/report/PrintableTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  Cash:          'Cash',
  Cheque:        'Cheque',
  BankTransfer:  'Bank Transfer',
  CreditCard:    'Credit Card',
  DebitCard:     'Debit Card',
  OnlineBanking: 'Online Banking',
  Other:         'Other',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '130px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:          { bg: '#f3f4f6', text: '#374151' },
    Submitted:      { bg: '#dbeafe', text: '#1e40af' },
    PendingApproval:{ bg: '#fef9c3', text: '#854d0e' },
    Approved:       { bg: '#d1fae5', text: '#065f46' },
    Rejected:       { bg: '#fee2e2', text: '#991b1b' },
    Posted:         { bg: '#a7f3d0', text: '#064e3b' },
    Cancelled:      { bg: '#f3f4f6', text: '#6b7280' },
    Reversed:       { bg: '#ede9fe', text: '#5b21b6' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ApPaymentVoucherProps {
  payment:     ApPaymentDto;
  companyName: string;
  printedBy?:  string;
}

export const ApPaymentVoucher = ({
  payment,
  companyName,
  printedBy,
}: ApPaymentVoucherProps): ReactNode => {

  // What this voucher settles (ML-08). Printed so the counterparty can see which of their
  // documents the money was applied to — a voucher that says only "5,000 paid" leaves them to
  // guess, which is how a remittance ends up reconciled against the wrong invoice.
  const allocationColumns = [
    { key: 'lineNumber',      label: '#',            width: '28px', align: 'center' as const,
      render: (a: ApPaymentAllocationDto) => a.lineNumber },
    { key: 'documentNumber',  label: 'Bill No.',    width: '120px',
      render: (a: ApPaymentAllocationDto) => a.documentNumber },
    { key: 'documentDate',    label: 'Date',         width: '80px',
      render: (a: ApPaymentAllocationDto) => a.documentDate ? fmtDate(a.documentDate) : '' },
    { key: 'dueDate',         label: 'Due',          width: '80px',
      render: (a: ApPaymentAllocationDto) => a.dueDate ? fmtDate(a.dueDate) : '' },
    { key: 'remarks',         label: 'Remarks',
      render: (a: ApPaymentAllocationDto) => a.remarks || '' },
    { key: 'allocatedAmount', label: 'Settled',      width: '90px', align: 'right' as const,
      render: (a: ApPaymentAllocationDto) => <strong>{fmtNum(a.allocatedAmount)}</strong> },
  ];

  const lineColumns = [
    { key: 'lineNumber',   label: '#',           width: '28px',  align: 'center' as const,
      render: (l: ApPaymentLineDto) => l.lineNumber },
    { key: 'paymentType',  label: 'Type',         width: '100px',
      render: (l: ApPaymentLineDto) => PAYMENT_TYPE_LABELS[l.paymentType] ?? l.paymentType },
    { key: 'chequeNumber', label: 'Cheque No.',   width: '90px',
      render: (l: ApPaymentLineDto) => l.chequeNumber || '' },
    { key: 'chequeDate',   label: 'Cheque Date',  width: '80px',
      render: (l: ApPaymentLineDto) => l.chequeDate ? fmtDate(l.chequeDate) : '' },
    { key: 'cardReference',label: 'Ref / Card',   width: '100px',
      render: (l: ApPaymentLineDto) => l.cardReference || '' },
    { key: 'currencyCode', label: 'CCY',          width: '40px',
      render: (l: ApPaymentLineDto) => l.currencyCode },
    { key: 'exchangeRate', label: 'FX Rate',      width: '70px',  align: 'right' as const,
      render: (l: ApPaymentLineDto) => fmtNum(l.exchangeRate, 4) },
    { key: 'amount',       label: 'Amount',       width: '90px',  align: 'right' as const,
      render: (l: ApPaymentLineDto) => <strong>{fmtNum(l.amount)}</strong> },
    { key: 'baseAmount',   label: 'Base Amt',     width: '90px',  align: 'right' as const,
      render: (l: ApPaymentLineDto) => fmtNum(l.baseAmount) },
    { key: 'remarks',      label: 'Remarks',
      render: (l: ApPaymentLineDto) => l.remarks || '' },
  ];

  const workflowRows = [
    payment.submittedBy ? { role: 'Submitted By', name: payment.submittedBy, date: payment.submittedDate } : null,
    payment.approvedBy  ? { role: 'Approved By',  name: payment.approvedBy,  date: payment.approvedDate  } : null,
    payment.postedBy    ? { role: 'Posted By',     name: payment.postedBy,    date: payment.postedDate    } : null,
  ].filter(Boolean) as { role: string; name: string; date: string | null | undefined }[];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
              {payment.isReversal ? 'Reversal Payment' : 'Supplier Payment Voucher'}
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {payment.paymentNumber}
            </div>
            <div style={{ marginTop: '4px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
              <StatusBadge status={payment.workflowStatus} />
              {payment.isReversal && (
                <span style={{
                  display: 'inline-block', padding: '1px 7px', borderRadius: '10px',
                  fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                  background: '#fef3c7', color: '#92400e',
                }}>
                  Reversal Payment
                </span>
              )}
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Supplier + Document details ─────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Pay To
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{payment.supplierName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{payment.supplierCode}</div>
          {payment.reference && (
            <div style={{ fontSize: '10px', color: '#374151', marginTop: '4px' }}>
              <span style={{ color: '#6b7280' }}>Reference:</span> {payment.reference}
            </div>
          )}
        </div>
        <div>
          <LabelValue label="Payment Date"   value={fmtDate(payment.paymentDate)} />
          <LabelValue label="Posting Date"   value={fmtDate(payment.postingDate)} />
          <LabelValue label="Payment Method" value={PAYMENT_TYPE_LABELS[payment.paymentMethod] ?? payment.paymentMethod} />
          <LabelValue label="Currency"       value={`${payment.currencyCode} (Rate: ${payment.exchangeRate.toFixed(4)})`} />
          <LabelValue label="Period"         value={`${payment.financialPeriodCode} / ${payment.financialYearCode}`} />
          <LabelValue label="Posting Status" value={payment.postingStatus} />
        </div>
      </div>

      {/* Narration */}
      {payment.narration && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Narration: </span>
          {payment.narration}
        </div>
      )}

      {/* ── Lines table ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Payment Details
      </div>

      <PrintableTable
        columns={lineColumns}
        rows={payment.lines ?? []}
        rowKey={(l) => l.id}
      />

      {/* ── Totals ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '4px',
        marginBottom: '20px',
      }}>
        <div style={{ width: '220px' }}>
          {(payment.withholdingAmount ?? 0) > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: '10px', color: '#374151' }}>
                <span>Paid ({payment.currencyCode}):</span>
                <span style={{ fontFamily: 'monospace' }}>{fmtNum(payment.totalAmount - (payment.withholdingAmount ?? 0))}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: '10px', color: '#374151' }}>
                <span>Withheld {payment.withholdingRuleCode ?? ''} ({payment.withholdingPercent ?? 0}%):</span>
                <span style={{ fontFamily: 'monospace' }}>{fmtNum(payment.withholdingAmount ?? 0)}</span>
              </div>
            </>
          )}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '5px 8px',
            background: '#1e3a5f',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
          }}>
            <span>{(payment.withholdingAmount ?? 0) > 0 ? 'Settles' : 'Total'} ({payment.currencyCode}):</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtNum(payment.totalAmount)}</span>
          </div>
          {payment.exchangeRate !== 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
              <span>Base Amount:</span>
              <span style={{ fontFamily: 'monospace' }}>{fmtNum(payment.baseAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Bills Settled (ML-08) ───────────────────────────────────────────── */}
      {(payment.allocations?.length ?? 0) > 0 && (
        <>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Bills Settled
          </div>

          <PrintableTable
            columns={allocationColumns}
            rows={payment.allocations ?? []}
            rowKey={(a) => a.id}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px', marginBottom: '20px' }}>
            <div style={{ width: '220px', display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', borderTop: '1px solid #1e3a5f' }}>
              <span>Total Settled ({payment.currencyCode}):</span>
              <span style={{ fontFamily: 'monospace' }}>
                {fmtNum((payment.allocations ?? []).reduce((t, a) => t + a.allocatedAmount, 0))}
              </span>
            </div>
          </div>
        </>
      )}

      {/* ── Workflow Trail ───────────────────────────────────────────────────── */}
      {workflowRows.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Workflow Trail
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {workflowRows.map((row) => (
                <tr key={row.role} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 8px', color: '#374151' }}>{row.role}</td>
                  <td style={{ padding: '4px 8px', color: '#111827', fontWeight: 500 }}>{row.name}</td>
                  <td style={{ padding: '4px 8px', color: '#374151', fontFamily: 'monospace' }}>{fmtDate(row.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {[
          { label: 'Prepared By' },
          { label: 'Approved By' },
          { label: 'Posted By' },
        ].map(({ label }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
